/**
 * Copyright (c) Mayoral Moda Infantil, S.A.U. and affiliates.
 */

import { InMemoryCache, makeVar } from '@apollo/client';
import { readFromCookies, writeToCookies } from '@utils/cookies';
import { DefaultLang, DefaultLangIdentifier } from '@utils/i18n';
import { firstFirstAvailableSeason } from '@utils/season';

import { LangIdentifiers, Season, StorageTokens } from '@/lib/constants';
import { Season as TSeason } from '@/types/global';
import PossibleTypes from '@/types/type-inspection';

export const loggedInVar = makeVar<boolean>(!!readFromCookies(StorageTokens.TOKEN));
export const langVar = makeVar<string>(DefaultLang);
export const langIdVar = makeVar<LangIdentifiers>(DefaultLangIdentifier);
export const usernameVar = makeVar(readFromCookies<string>(StorageTokens.USERNAME));
export const isManagerVar = makeVar(!!readFromCookies(StorageTokens.IS_MANAGER));
export const seasonVar = makeVar(readFromCookies<Season>(StorageTokens.SELECTED_SEASON));
export const workshopVar = makeVar(readFromCookies<number>(StorageTokens.SELECTED_WORKSHOP));

const getCacheConfiguration = (): InMemoryCache => {
  return new InMemoryCache({
    possibleTypes: PossibleTypes.possibleTypes,
    typePolicies: {
      Customer: {
        fields: {
          username() {
            return usernameVar();
          },
          season(_, { readField }) {
            const seasons = readField<TSeason[]>('seasons');
            if (!seasons) {
              // If the customer has not resolved yet, just return
              return null;
            }

            let season = seasonVar();
            if (!season) {
              // If no value was set, set the first available season
              // and update the cache var.
              season = firstFirstAvailableSeason(seasons as TSeason[]);
              seasonVar(season);
            }

            // Persist the current season value
            writeToCookies(StorageTokens.SELECTED_SEASON, season);

            return season;
          },
          loggedIn() {
            return loggedInVar();
          },
        },
      },
      Query: {
        merge: true,
      },
      // Size: pos_talla can repeat across colors with different name values (M/L/XL vs 2/4/6).
      Size: {
        keyFields: false,
      },
      Mutation: {
        fields: {
          login: {
            // clears out sensitive login credentials from being stored in client-side cache
            // see: https://github.com/apollographql/apollo-client/issues/3592
            keyArgs: () => ['login'],
          },
        },
      },
    },
  });
};

export { getCacheConfiguration };
